import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography, Button } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import SnackBars from "../snackbars/SnackBars";
import CloudDownload from "@mui/icons-material/CloudDownload";
import {
  downloadAttachment,
  getAttachmentsByProjectCode,
} from "../../services/AttachmentService/AttachmentService";
import { saveAs } from "file-saver";
import DynamicTable from "../../components/Tables/DynamicTable";

const theme = createTheme({
  typography: {
    fontSize: 12,
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiButton: {
      defaultProps: {
        size: "small",
      },
    },
  },
});

const AttachmentTable = ({ projectCode }) => {
  const [attachmentList, setAttachmentList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState([
    { field: "", action: "", value: "" },
  ]);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState({
    title: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const hasValidFilters = () =>
    filters.some((filter) => filter.field && filter.action && filter.value);

  useEffect(() => {
    fetchAttachments();
  }, [filters]);

  const fetchAttachments = async () => {
    try {
      setIsLoading(true);
      const attachmentResponse = await getAttachmentsByProjectCode(projectCode);
      if (attachmentResponse.message === "success") {
        setAttachmentList(attachmentResponse.files);
      }
    } catch (e) {
      setOpenSnackBar(true);
      if (e.response.status == 404) {
        setSnackBarMessage({
          title: "No attachments found",
          type: "warning",
        });
      } else {
        setSnackBarMessage({
          title: e.response?.data?.error || "Internal Server Error",
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const downloadImage = async (fileName) => {
    try {
      const response = await downloadAttachment(projectCode, fileName);
      const headers = response.headers || {};
      const contentType = headers["content-type"] || "application/octet-stream";
      let fileExtension = "";
      if (contentType.includes("image")) {
        fileExtension = ".jpg";
      } else if (contentType === "application/pdf") {
        fileExtension = ".pdf";
      }
      const suggestedFileName = headers["content-disposition"]
        ? headers["content-disposition"]
            .split("filename=")[1]
            .replace(/['"]/g, "")
        : fileName;

      saveAs(
        response.data,
        suggestedFileName || `downloaded_file${fileExtension}`
      );
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRemoveFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    fetchAttachments();
    setAnchorEl(null);
  };

  const handleCloseAnchor = () => {
    setFilters([{ field: "", action: "", value: "" }]);
    setAnchorEl(null);
  };

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "fileName",
        header: "File Name",
        size: 50,
      },
      {
        accessorKey: "lastModified",
        header: "Last Modified",
        size: 50,
      },
      {
        accessorKey: "size",
        header: "Size",
        size: 100,
        Cell: ({ row }) => {
          const sizeInBytes = row.original.size;
          const sizeInMB = sizeInBytes / (1024 * 1024);

          if (sizeInMB < 0.2) {
            const sizeInKB = (sizeInBytes / 1024).toFixed(2);
            return `${sizeInKB} KB`;
          }

          return `${sizeInMB.toFixed(2)} MB`;
        },
      },
      {
        accessorKey: "contentType",
        header: "Content Type",
        size: 100,
      },
      {
        accessorKey: "activity_name",
        header: "Activity Name",
        size: 100,
      },
      {
        accessorKey: "attachment_category_name",
        header: "Attachment Category",
        size: 100,
      },
      {
        header: "Download Attachment",
        size: 100,
        muiTableHeadCellProps: {
          align: "center",
        },
        Cell: ({ row }) => (
          <Typography
            sx={{
              cursor: "pointer",
              textAlign: "center",
            }}
            onClick={() => downloadImage(row.original.fileName)}
          >
            <CloudDownload
              sx={{ fontSize: "30px", cursor: "pointer", color: "#b41660" }}
            />
          </Typography>
        ),
      },
    ];
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={12} md={6}></Grid>
        <Grid item xs={12}>
          <DynamicTable
            columns={columns}
            data={attachmentList}
            isFilterEnable={true}
            isSearchBarEnable={true}
            isEnableSorting={true}
            isHeaderBackgroundColor={false}
            showTableTopToolbar={true}
            enableColumnActions={true}
          />
        </Grid>
      </Grid>
      <SnackBars
        open={openSnackBar}
        setOpen={setOpenSnackBar}
        handleClose={handleCloseSnackBar}
        title={snackBarMessage.title}
        type={snackBarMessage.type}
      />
    </ThemeProvider>
  );
};

export default AttachmentTable;
